import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  Suspense,
} from "react";
import * as THREE from "three";
import { useThree, useLoader } from "@react-three/fiber";
import ProjectedMaterial from "three-projected-material";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader";
import { TextureLoader } from "three/src/loaders/TextureLoader";
import { mergeBufferGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import useStore from "./Store";

function Box({ position, color }) {
  const ref = useRef();

  return (
    <mesh position={position} ref={ref}>
      <boxBufferGeometry args={[1, 1, 1]} attach="geometry" />
      <meshPhongMaterial color={color ? color : "#18a36e"} attach="material" />
    </mesh>
  );
}

function Scene({
  textureUrl,
  meshUrl,
  allMaterials,
  setAllMaterials,
  setRenderer,
  setCamera,
  setScene,
  modelName,
}) {
  //Zustand Hooks
  const currentMaterial = useStore((state) => state.currentMaterial);

  const mesh = useRef();
  // load the model
  const { gl, scene, camera, size, mouse, controls } = useThree();

  const texture = useLoader(TextureLoader, textureUrl);

  //loading manager
  const manager = new THREE.LoadingManager();
  manager.onStart = function (url, itemsLoaded, itemsTotal) {
    console.log(
      "Started loading file: " +
        url +
        ".\nLoaded " +
        itemsLoaded +
        " of " +
        itemsTotal +
        " files."
    );
  };

  manager.onLoad = function () {
    console.log("Loading complete!");
  };

  manager.onProgress = function (url, itemsLoaded, itemsTotal) {
    console.log(
      "Loading file: " +
        url +
        ".\nLoaded " +
        itemsLoaded +
        " of " +
        itemsTotal +
        " files."
    );
  };

  manager.onError = function (url) {
    console.log("There was an error loading " + url);
  };
  const loaderGLTF = new GLTFLoader(manager);
  const loaderOBJ = new OBJLoader(manager);

  //switch loader depending on file extension
  var Loader = GLTFLoader;
  var testLoader = loaderGLTF;
  if (modelName === "glb") {
    Loader = GLTFLoader;
    testLoader = loaderGLTF;
  } else if (modelName === "obj") {
    Loader = OBJLoader;
    testLoader = loaderOBJ;
  } else {
    console.log("not supported filetype");
  }

  const gltf = useLoader(
    Loader,
    meshUrl,
    (onprogress = (ev) => {
      console.log(ev);
    })
  );
  const geometry = extractGeometry(modelName === "obj" ? gltf : gltf.scene);
  geometry.clearGroups();
  //var geometry;

  /*testLoader.load( meshUrl, (gltf) => {
       //switch based on glb or obj
    geometry = extractGeometry(modelName === 'obj' ? gltf : gltf.scene);
    geometry.clearGroups();

    if (geometry) {
      const materials = [];
      // make an array of materials with the same texture
      for (let i = 0; i < 10; i++) {
        const material = new ProjectedMaterial({
          camera: camera.clone(),
          texture,
          color: "#fff",
          textureScale: 0.8,
          metalness: 0.5,
          roughness: 0.6,
          reflectivity: 1.0,
          // needed for multiple materials
          transparent: true,
          map: leeMapCol,
          normalMap: leeSmoothUV,
        });
  
        materials.push(material);
        setAllMaterials(...allMaterials, material);
        // reserve a group for the material on the geometry
        // https://stackoverflow.com/a/49708915
        geometry.addGroup(0, Infinity, i);
  
        mesh.current.geometry = geometry;
        mesh.current.material = materials;
  
        // since we're not projecting straight away,
        // we need to tell the other materials to be transparent
        // until they're projected on
        mesh.current.material.forEach((material, i) => {
          if (i === 0) {
            return;
          }
  
          material.uniforms.backgroundOpacity.value = 0;
        });
  
        // move the mesh any way you want!
        mesh.current.scale.setScalar(0.6);
        mesh.current.rotation.y = Math.random() * Math.PI * 2;
      }
      setAllMaterials(materials);
    };
  });*/

  const leeMapCol = useLoader(
    TextureLoader,
    "assets/LeePerrySmith/Map-COL.jpeg"
  );
  const leeSmoothUV = useLoader(
    TextureLoader,
    "assets/LeePerrySmith/Infinite-Level_02_Tangent_SmoothUV.jpeg"
  );

  const [startX, setStartX] = useState(undefined);
  const [startY, setStartY] = useState(undefined);
  const [percentLoaded, setPercentLoaded] = useState(100);

  const position = useStore((state) => state.position);
  const oldPosition = useStore((state) => state.oldPosition);
  const rotation = useStore((state) => state.rotation);
  const oldRotation = useStore((state) => state.oldRotation);
  const materialScale = useStore((state) => state.materialScale);

  //color
  const color = useStore((state) => state.color);
  const setColor = useStore((state) => state.setColor);

  useEffect(() => {
    setRenderer(gl);
    setScene(scene);
    setCamera(camera);
  }, [gl, scene, camera]);

  useEffect(() => {
    if (
      mesh.current.material[currentMaterial] !== undefined &&
      allMaterials.length > 0
    ) {
      mesh.current.material[currentMaterial].camera.position.set(
        mesh.current.material[currentMaterial].camera.position.x +
          (position[0] - oldPosition[0]) / 10,
        mesh.current.material[currentMaterial].camera.position.y +
          (position[1] - oldPosition[1]) / 10,
        mesh.current.material[currentMaterial].camera.position.z +
          (position[2] - oldPosition[2]) / 10
      );
      mesh.current.material[currentMaterial].project(mesh.current);
    }
  }, [position]);

  useEffect(() => {
    if (
      mesh.current.material[currentMaterial] !== undefined &&
      allMaterials.length > 0
    ) {
      mesh.current.material[currentMaterial].camera.rotation.set(
        mesh.current.material[currentMaterial].camera.rotation.x +
          (rotation[0] - oldRotation[0]) / 10,
        mesh.current.material[currentMaterial].camera.rotation.y +
          (rotation[1] - oldRotation[1]) / 10,
        mesh.current.material[currentMaterial].camera.rotation.z +
          (rotation[2] - oldRotation[2]) / 10
      );
      mesh.current.material[currentMaterial].project(mesh.current);
    }
  }, [rotation]);

  useEffect(() => {
    mesh.current.material = allMaterials;
  }, [allMaterials]);

  useLayoutEffect(() => {
    const materials = [];
    // make an array of materials with the same texture
    for (let i = 0; i < 10; i++) {
      var material;
      if (color === "#0000") {
        material = new ProjectedMaterial({
          camera: camera.clone(),
          texture,
          color: "#fff",
          textureScale: 0.8,
          metalness: 0.5,
          roughness: 0.6,
          reflectivity: 1.0,
          // needed for multiple materials
          transparent: true,
          map: leeMapCol,
          normalMap: leeSmoothUV,
        });
      } else {
        material = new ProjectedMaterial({
          camera: camera.clone(),
          texture,
          color: color,
          textureScale: 0.8,
          metalness: 0.5,
          roughness: 0.6,
          reflectivity: 1.0,
          // needed for multiple materials
          transparent: true,
        });
      }
      materials.push(material);
      setAllMaterials(...allMaterials, material);
      // reserve a group for the material on the geometry
      // https://stackoverflow.com/a/49708915
      geometry.addGroup(0, Infinity, i);

      mesh.current.geometry = geometry;
      mesh.current.material = materials;

      // since we're not projecting straight away,
      // we need to tell the other materials to be transparent
      // until they're projected on
      mesh.current.material.forEach((material, i) => {
        if (i === 0) {
          return;
        }

        material.uniforms.backgroundOpacity.value = 0;
      });

      // move the mesh any way you want!
      mesh.current.scale.setScalar(0.6);
      mesh.current.rotation.y = Math.random() * Math.PI * 2;
    }
    setAllMaterials(materials);
  }, [color]);

  useEffect(() => {
    if (mesh.current.material[currentMaterial] !== undefined) {
      mesh.current.material[currentMaterial].textureScale = materialScale;
    }
  }, [materialScale]);

  ///Projector Rotation TODO

  //texture upload
  useEffect(() => {
    if (mesh.current.material[currentMaterial] !== undefined) {
      mesh.current.material[currentMaterial].texture = texture;
    }
  }, [textureUrl]);

  function onProgress(xhr) {
    if (xhr.lengthComputable) {
      const percentComplete = (xhr.loaded / xhr.total) * 100;
      console.log("model " + Math.round(percentComplete, 2) + "% downloaded");
      setPercentLoaded(percentComplete);
    }
    setPercentLoaded(100);
  }

  //model upload
  useEffect(() => {
    setPercentLoaded(0);
    testLoader.load(
      meshUrl,
      (gltf) => {
        //switch based on glb or obj
        const geometry = extractGeometry(
          modelName === "obj" ? gltf : gltf.scene
        );
        geometry.clearGroups();

        // Secondary model extract geometry
        for (let i = 0; i < 10; i++) {
          geometry.addGroup(0, Infinity, i);
        }
        mesh.current.geometry = geometry;
        //set initial position to origin
        mesh.current.position.set(0, 0, 0);
        //fancy math to recenter and scale model
        //create box3 to find center of object
        const box3 = new THREE.Box3().setFromObject(mesh.current);
        const vector = new THREE.Vector3();
        box3.getCenter(vector);
        //adjust object position so its centered
        mesh.current.position.set(-vector.x, -vector.y, -vector.z);
        // Compute necessary camera parameters
        const fov = camera.fov;
        const aspect = camera.aspect; // camera aspect ratio (width / height)
        // three.js stores camera fov as vertical fov. We need to calculate horizontal fov
        const hfov =
          (2 *
            Math.atan(Math.tan(THREE.MathUtils.degToRad(fov) / 2) * aspect) *
            180) /
          Math.PI;
        // calculate the distance from the camera at which the frustum is the same width as your model
        const size = new THREE.Vector3();
        box3.getSize(size);
        const dist =
          (size.x * 0.5) / Math.tan(THREE.MathUtils.degToRad(hfov * 0.5));
        // Position camera exactly based on model. There are more elegant ways to do this, but this is a quick and dirty solution
        // Camera looks down its own negative Z axis, adding Z effectively "zooms out"
        camera.position.copy(mesh.current.position);
        camera.translateZ(dist * 2);
      },
      onProgress
    );
  }, [meshUrl]);

  return (
    <mesh
      ref={mesh}
      recieveShadow
      onPointerDown={(event) => {
        setStartX(event.offsetX);
        setStartY(event.offsetY);
      }}
      // project the texture on click
      onPointerUp={(event) => {
        if (!event.isPrimary) return;

        let dragCoords = {
          ...(startX !== undefined && { dragX: event.offsetX - startX }),
          ...(startY !== undefined && { dragY: event.offsetY - startY }),
        };

        //mesh.current.material[currentMaterial].texture = useLoader(TextureLoader, textureUrl);

        const { dragX, dragY } = dragCoords;
        // return if we used the orbit controls
        if (Math.hypot(dragX, dragY) > 2) {
          return;
        }
        //copy webgl camera position for new placements
        mesh.current.material[currentMaterial].camera.copy(camera);
        // project the texture
        mesh.current.material[currentMaterial].project(mesh.current);

        // move the texture to where we clicked with the mouse
        const center = new THREE.Vector2(size.width / 2, size.height / 2);
        const mouse = new THREE.Vector2(event.offsetX, event.offsetY);

        const offset = new THREE.Vector2().subVectors(center, mouse);

        // // convert it to 0 to 1 range
        offset.divide(new THREE.Vector2(size.width, size.height));

        // convert from window coordinate system to WebGL
        // coordinate system
        offset.y *= -1;

        mesh.current.material[currentMaterial].textureOffset = offset;
        //setMaterialScale(0.8);
        // increment the index
        console.log(mesh.current.material);
        setAllMaterials([...mesh.current.material]);
        setStartX(undefined);
        setStartY(undefined);
      }}
    ></mesh>
  );
}
/*{allMaterials.map((b) => (
  <Box key={Math.random()} lookAt={mesh} position={b.camera.position} />
  ))}*/

// extract all geometry from a gltf scene
function extractGeometry(gltf) {
  const geometries = [];
  gltf.traverse((child) => {
    if (child.isMesh) {
      geometries.push(child.geometry);
    }
  });

  return mergeBufferGeometries(geometries);
}

export default Scene;
